import { DEFAULT_CAPTURE_WIDTH } from './constants';
import { logCamera } from './debug';

/**
 * The one generic, low-resolution request allowed to run with no known
 * device/facing preference at all — the sole permission-establishing
 * getUserMedia call. Its resulting stream is kept and reused (see
 * store.js's requestPermission/initializeCamera), never thrown away and
 * re-requested, which is what collapsed the old flow's 2-3 redundant
 * preflight getUserMedia calls on every /camera-permission mount into one.
 */
export const GENERIC_PERMISSION_VIDEO_CONSTRAINTS = {
  video: {
    facingMode: 'user',
    width: { ideal: 640 },
    height: { ideal: 480 },
  },
};

/**
 * Real-capture constraints (post-permission acquisition/switch/restore).
 *
 * Deliberately no `height`: constraining both axes lets the browser satisfy
 * the request by cropping the sensor frame to the requested aspect ratio,
 * and `videoWidth`/`videoHeight` then report that cropped geometry instead
 * of the camera's own — the exact reason the pre-CameraManager
 * `videoConstraints` helper this replaces only ever set `width`, paired with
 * `resizeMode: 'none'` to ask for a native capture mode outright. Carried
 * forward here rather than re-introducing that bug.
 *
 * `deviceId` and `facingMode` are structurally never combined in one
 * constraints object — asserting a hardcoded facing mode alongside an exact
 * deviceId is what made picking a rear camera from the device list a likely
 * OverconstrainedError on Safari in the old implementation.
 */
function buildRealCaptureConstraints({ deviceId, facingMode, width }) {
  const video = {
    width: { ideal: width || DEFAULT_CAPTURE_WIDTH },
    resizeMode: { ideal: 'none' },
  };

  if (deviceId) {
    video.deviceId = { exact: deviceId };
  } else {
    video.facingMode = facingMode || 'user';
  }

  return { video };
}

/**
 * The deviceId -> facingMode fallback chain — never a bare
 * `{ deviceId: { exact } }` request with no recovery path:
 *
 *   1. `deviceId`, if given.
 *   2. `facingMode` (defaults to 'user'), if (1) is absent or throws.
 *
 * Whichever attempt succeeds wins; if the facingMode attempt also throws,
 * that error propagates to the caller — initializeCamera surfaces it as a
 * real failure, switchCamera leaves the previous stream running untouched
 * (see store.js for both).
 */
export async function openStreamWithFallback({ deviceId, facingMode, width }) {
  if (deviceId) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        buildRealCaptureConstraints({ deviceId, width })
      );
      return { stream, attempt: 'deviceId' };
    } catch (error) {
      logCamera('acquire.deviceId-failed', { deviceId, errorName: error?.name });
    }
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia(
      buildRealCaptureConstraints({ facingMode, width })
    );
    return { stream, attempt: 'facingMode' };
  } catch (error) {
    logCamera('acquire.facingMode-failed', {
      facingMode: facingMode || 'user',
      errorName: error?.name,
    });
    throw error;
  }
}
